// ===========================================
// Kanban Column - SDLC Stage Column Container
// ===========================================
// Now supports being dragged to reorder columns left/right
// Users can customize their board layout by dragging column headers
// Collapse state is managed by parent and persisted to user profile

import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import KanbanCard from './KanbanCard';

// isCollapsed and onToggleCollapse are passed from KanbanBoard for persistence
const KanbanColumn = ({ column, projects, isCollapsed = false, onToggleCollapse }) => {
  
  // ===========================================
  // Setup sortable for column reordering
  // ===========================================
  // This allows the entire column to be dragged horizontally
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: `column-${column.id}`, // Prefixed ID to distinguish from card IDs
    data: { type: 'column', column }
  });

  // ===========================================
  // Setup droppable zone for cards in this column
  // ===========================================
  // Cards can be dropped into this column to change their status
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: column.id,
    data: { column }
  });

  // Get project IDs for sortable context (cards within this column)
  const projectIds = projects.map(p => p._id || p.id);

  // ===========================================
  // Apply transform styles for dragging animation
  // ===========================================
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setSortableRef}
      style={style}
      className={`kanban-column ${isOver ? 'kanban-column--over' : ''} ${isCollapsed ? 'kanban-column--collapsed' : ''} ${isDragging ? 'kanban-column--dragging' : ''}`}
    >
      {/* Column Header - includes drag handle for reordering columns */}
      <div className="kanban-column__header">
        {/* Drag handle for column reordering - grab this to move the column */}
        <div 
          className="kanban-column__drag-handle"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={14} />
        </div>
        
        {/* Status indicator dot */}
        <div 
          className="kanban-column__status-dot"
          style={{ background: column.color }}
        />
        
        {/* Column title - click to collapse/expand */}
        <h3 
          className="kanban-column__title"
          onClick={() => onToggleCollapse?.(column.id)}
        >
          {column.label}
        </h3>
        
        {/* Project count badge */}
        <span className="kanban-column__count">{projects.length}</span>
        
        {/* Collapse toggle button */}
        <button 
          className="kanban-column__toggle"
          onClick={() => onToggleCollapse?.(column.id)}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Column Content - Droppable Area for cards */}
      {!isCollapsed && (
        <div 
          ref={setDroppableRef}
          className="kanban-column__content"
        >
          <SortableContext items={projectIds} strategy={verticalListSortingStrategy}>
            {projects.length > 0 ? (
              projects.map(project => (
                <KanbanCard 
                  key={project._id || project.id} 
                  project={project}
                />
              ))
            ) : (
              <div className="kanban-column__empty">
                <span>No projects</span>
                <p>Drag projects here</p>
              </div>
            )}
          </SortableContext>
        </div>
      )}

      {/* Collapsed indicator showing count when column is minimized */}
      {isCollapsed && projects.length > 0 && (
        <div className="kanban-column__collapsed-indicator">
          <span>{projects.length} project{projects.length !== 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
};

export default KanbanColumn;
